import { useMemo, useState } from 'react';
import { KNOWLEDGE_CATEGORIES, type KnowledgeCategory, type KnowledgeResources } from '@shared/types';
import { ArchNav } from '../components/ArchNav';
import { Freshness, SourceBadge } from '../components/KnowledgeAnswer';
import { PageHeader } from '../components/PageHeader';
import { EmptyState, LoadingPage, Tag } from '../components/ui';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useAsync } from '../lib/useAsync';

type Source = KnowledgeResources['sources'][number];

function categoryLabel(value: string): string {
  return KNOWLEDGE_CATEGORIES[value as KnowledgeCategory] ?? value;
}

/** Published HOA documents and official city/county resources, grouped and filterable. */
export function ArchResourcesPage() {
  const { user, isReviewer } = useAuth();
  const state = useAsync(() => api.archResources(), [user?.communityId]);
  const [category, setCategory] = useState<string>('all');

  const data = state.data;
  const categories = useMemo(() => {
    const used = new Set<string>([...(data?.sources ?? []).map((s) => s.category), ...(data?.documents ?? []).map((d) => d.category)]);
    return [...used].sort((a, b) => categoryLabel(a).localeCompare(categoryLabel(b)));
  }, [data]);

  if (state.loading && !data) return <LoadingPage label="Loading resources…" />;
  if (!data) return <p className="mut">Could not load resources.</p>;

  const matches = (value: string) => category === 'all' || value === category;
  const documents = data.documents.filter((doc) => matches(doc.category));
  const hoaSources = data.sources.filter((source) => source.authority === 'hoa' && matches(source.category));
  const officialSources = data.sources.filter((source) => source.authority !== 'hoa' && matches(source.category));
  const j = data.jurisdiction;
  const place = [j.municipality, j.county, j.stateRegion].filter(Boolean).join(', ');

  return (
    <div>
      <PageHeader
        title="Resources"
        lede="HOA governing documents and official city or county resources your board has published. Rules change: check the date each was last verified."
      />
      <ArchNav />

      {place ? (
        <p className="mut">
          Jurisdiction: <strong>{place}</strong>{' '}
          {j.verifiedAt ? <Tag tone="ok">Verified by the board</Tag> : <Tag tone="pend">Not yet verified</Tag>}{' '}
          Your mailing city may differ from the authority that issues permits for your lot.
        </p>
      ) : (
        <p className="mut">The board has not set this community’s city or county yet.</p>
      )}

      {categories.length > 1 && (
        <div className="chip-row" role="radiogroup" aria-label="Filter by topic">
          {['all', ...categories].map((value) => (
            <button
              key={value}
              type="button"
              role="radio"
              aria-checked={category === value}
              className={`chip${category === value ? ' on' : ''}`}
              onClick={() => setCategory(value)}
            >
              {value === 'all' ? 'All topics' : categoryLabel(value)}
            </button>
          ))}
        </div>
      )}

      {documents.length + hoaSources.length + officialSources.length === 0 ? (
        <EmptyState
          title={category === 'all' ? 'No resources have been published yet.' : 'Nothing published on this topic.'}
          hint={isReviewer ? 'Add and publish sources under Knowledge Sources.' : 'Ask the board which documents apply to your project.'}
        />
      ) : (
        <>
          {(documents.length > 0 || hoaSources.length > 0) && (
            <section aria-labelledby="hoa-resources">
              <h2 id="hoa-resources">HOA documents</h2>
              <div className="kc-grid">
                {documents.map((doc) => (
                  <article key={doc.id} className="card kc-card">
                    <div className="tag-row">
                      <SourceBadge section="hoa" official />
                      <Tag>{categoryLabel(doc.category)}</Tag>
                      {doc.visibility === 'committee' && <Tag tone="warn">Committee only</Tag>}
                    </div>
                    <h3>{doc.title}</h3>
                    <div className="kc-card-actions">
                      <a className="btn s tiny" href={doc.url} target="_blank" rel="noopener noreferrer">
                        Open document
                      </a>
                    </div>
                  </article>
                ))}
                {hoaSources.map((source) => (
                  <ResourceCard key={source.id} source={source} />
                ))}
              </div>
            </section>
          )}
          {officialSources.length > 0 && (
            <section aria-labelledby="official-resources">
              <h2 id="official-resources">City, county and other official resources</h2>
              <div className="kc-grid">
                {officialSources.map((source) => (
                  <ResourceCard key={source.id} source={source} />
                ))}
              </div>
            </section>
          )}
        </>
      )}
    </div>
  );
}

function ResourceCard({ source }: { source: Source }) {
  const linkOnly = source.ingestionMode === 'link_only' || source.status === 'link_only';
  return (
    <article className="card kc-card">
      <div className="tag-row">
        <SourceBadge section={source.authority === 'hoa' ? 'hoa' : 'municipal'} official={source.official} authorityName={source.authorityName} />
        {linkOnly && <Tag tone="pend">Link only</Tag>}
        <Tag>{categoryLabel(source.category)}</Tag>
        {source.visibility === 'committee' && <Tag tone="warn">Committee only</Tag>}
      </div>
      <h3>{source.title}</h3>
      {source.authorityName && <div className="mut">{source.authorityName}</div>}
      {source.description && <p style={{ margin: 0 }}>{source.description}</p>}
      {linkOnly && source.linkOnlyReason && <p className="mut" style={{ margin: 0 }}>{source.linkOnlyReason}</p>}
      <Freshness checkedAt={source.lastCheckedAt} stale={source.stale} />
      <div className="kc-card-actions">
        <a className="btn s tiny" href={source.url} target="_blank" rel="noopener noreferrer">
          {source.category === 'property_lookup' ? 'Open official property lookup' : 'Open official source'}
          <span className="sr-only"> (opens {new URL(source.url).hostname})</span>
        </a>
      </div>
    </article>
  );
}
